import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { problemBankApi } from '@/lib/problemBankApi';
import { ExternalSubmission } from '@/types/problemBank';
import {
  ArrowLeft,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Mail,
  Phone,
  FileText,
  AlertTriangle,
  RefreshCw,
  Download,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  User,
  Calendar,
  Filter,
  Trash2,
  Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  under_review: 'bg-blue-100 text-blue-700 border-blue-200',
  accepted: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200'
};

export default function AdminSubmissions() {
  const navigate = useNavigate();
  const { user, allUsers } = useAuth();
  const { toast } = useToast();
  
  const [submissions, setSubmissions] = useState<ExternalSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<ExternalSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<ExternalSubmission | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignAnalystDialogOpen, setAssignAnalystDialogOpen] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchQuery, statusFilter]);

  const loadSubmissions = async () => {
    setIsLoading(true);
    try {
      const data = await problemBankApi.getExternalSubmissions();
      setSubmissions(data);
      setFilteredSubmissions(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load submissions',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.companyName.toLowerCase().includes(query) ||
        s.contactName.toLowerCase().includes(query) ||
        s.contactEmail.toLowerCase().includes(query) ||
        s.problemTitle.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    setFilteredSubmissions(filtered);
  };

  const handleDelete = async () => {
    if (!selectedSubmission) return;
    
    try {
      // Add delete API call here
      toast({
        title: 'Success',
        description: 'Submission deleted successfully'
      });
      setDeleteDialogOpen(false);
      loadSubmissions();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete submission',
        variant: 'destructive'
      });
    }
  };

  const handleAssignAnalyst = async () => {
    if (!selectedSubmission || !selectedAnalyst) return;
    
    try {
      // Add assign API call here
      toast({
        title: 'Success',
        description: 'Analyst assigned successfully'
      });
      setAssignAnalystDialogOpen(false);
      loadSubmissions();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign analyst',
        variant: 'destructive'
      });
    }
  };

  const getReviewerName = (reviewerId?: string) => {
    if (!reviewerId) return 'Unassigned';
    const reviewer = allUsers.find(u => u.id === reviewerId);
    return reviewer?.name || 'Unknown';
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const analysts = allUsers.filter(u => u.role === 'analyst' || u.role === 'senior_analyst');

  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    underReview: submissions.filter(s => s.status === 'under_review').length,
    accepted: submissions.filter(s => s.status === 'accepted').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
    conversionRate: submissions.length > 0 
      ? Math.round((submissions.filter(s => s.status === 'accepted').length / submissions.length) * 100)
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/problem-bank')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Submission Management</h1>
          <p className="text-muted-foreground mt-1">
            Administer and oversee all external problem submissions
          </p>
        </div>
        <Button variant="outline" onClick={loadSubmissions}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Under Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.underReview}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Accepted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Rejected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.conversionRate}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by company, contact, or problem..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Problem</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead>Reviewed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-24" /></TableCell>
                  </TableRow>
                ))
              ) : filteredSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-muted-foreground">No submissions found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubmissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell className="text-sm">
                      {getTimeAgo(submission.submittedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{submission.companyName}</div>
                      {submission.companyRegistration && (
                        <div className="text-xs text-muted-foreground">
                          Reg: {submission.companyRegistration}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{submission.contactName}</div>
                      <div className="text-xs text-muted-foreground">{submission.contactTitle}</div>
                      <div className="text-xs text-muted-foreground">{submission.contactEmail}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{submission.problemTitle}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {submission.problemDescription}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[submission.status]}>
                        {submission.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {submission.reviewedBy ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback>
                              {getReviewerName(submission.reviewedBy).split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{getReviewerName(submission.reviewedBy)}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {submission.reviewedAt ? (
                        <span className="text-sm">{new Date(submission.reviewedAt).toLocaleDateString()}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!submission.reviewedBy && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setAssignAnalystDialogOpen(true);
                            }}
                          >
                            <User className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500"
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <p className="text-sm p-2 bg-muted rounded">{selectedSubmission.companyName}</p>
                </div>
                <div className="space-y-2">
                  <Label>Registration</Label>
                  <p className="text-sm p-2 bg-muted rounded">{selectedSubmission.companyRegistration || 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contact Information</Label>
                <div className="p-3 bg-muted rounded space-y-2">
                  <p className="text-sm font-medium">{selectedSubmission.contactName} ({selectedSubmission.contactTitle})</p>
                  <p className="text-sm">{selectedSubmission.contactEmail}</p>
                  <p className="text-sm">{selectedSubmission.contactPhone}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Problem Title</Label>
                <p className="text-sm p-2 bg-muted rounded">{selectedSubmission.problemTitle}</p>
              </div>

              <div className="space-y-2">
                <Label>Problem Description</Label>
                <p className="text-sm p-3 bg-muted rounded whitespace-pre-line">
                  {selectedSubmission.problemDescription}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Impact</Label>
                  <p className="text-sm p-2 bg-muted rounded">{selectedSubmission.estimatedImpact || 'Not specified'}</p>
                </div>
                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Badge className={
                    selectedSubmission.urgency === 'high' ? 'bg-red-100 text-red-700' :
                    selectedSubmission.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }>
                    {selectedSubmission.urgency}
                  </Badge>
                </div>
              </div>

              {selectedSubmission.documents && selectedSubmission.documents.length > 0 && (
                <div className="space-y-2">
                  <Label>Documents</Label>
                  <div className="space-y-2">
                    {selectedSubmission.documents.map((doc, i) => (
                      <a
                        key={i}
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded hover:bg-muted"
                      >
                        <FileText className="h-4 w-4" />
                        <span className="text-sm flex-1">Document {i + 1}</span>
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedSubmission.reviewNotes && (
                <div className="space-y-2">
                  <Label>Review Notes</Label>
                  <p className="text-sm p-3 bg-muted rounded">{selectedSubmission.reviewNotes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium">Submitted:</span> {new Date(selectedSubmission.submittedAt).toLocaleString()}
                </div>
                {selectedSubmission.reviewedAt && (
                  <div>
                    <span className="font-medium">Reviewed:</span> {new Date(selectedSubmission.reviewedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Analyst Dialog */}
      <Dialog open={assignAnalystDialogOpen} onOpenChange={setAssignAnalystDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Analyst</DialogTitle>
            <DialogDescription>
              Select an analyst to review this submission.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Analyst</Label>
              <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an analyst" />
                </SelectTrigger>
                <SelectContent>
                  {analysts.map(analyst => (
                    <SelectItem key={analyst.id} value={analyst.id}>
                      {analyst.name} ({analyst.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignAnalystDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignAnalyst} disabled={!selectedAnalyst}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this submission? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}